<template>
  <v-container class="pa-3 layout-container">
    <BaseHeader
      @filter="handleFilter"
      @filterButtonClick="handleFilterButtonClick"
      @uploadButtonClick="handleUploadButtonClick"
      :activeFiltersAmount="amountOfActiveFilters"
    />
    <div class="position-relative">
      <!-- Upload dialog -->
      <UploadDialog
        v-model="showUploadDialog"
        :topics="translatedTopics"
        :topics-loading="isLoading"
        :upload-progress="uploadProgress"
        :upload-complete="uploadComplete"
        :upload-error="uploadError"
        @upload-start="handleUploadStart"
      />

      <!-- Teleport filter dashboard for small screens -->
      <Teleport
        to="body"
        :disabled="!isSmallScreen"
      >
        <FilterDashboard
          v-if="showFilters && isSmallScreen"
          v-model:show="showFilters"
          @filter-change="handleFilter"
          @filter-reset="handleFilterReset"
          :active-filters="activeFilters"
          :topics="topics"
          :materials-amount="materialsAmount"
          :asset-types="assetTypesWithCounts"
          :image-sources="imageSourcesWithCounts"
          class="filter-overlay"
        />
      </Teleport>

      <!-- Layout container -->
      <div :class="isSmallScreen ? '' : 'd-flex flex-row ma-0 g-0'">
        <!-- Filter column - only for large screens -->
        <div
          v-if="!isSmallScreen && showFilters"
          class="filter-col"
        >
          <div class="filter-wrapper">
            <!-- Filter dashboard for large screens -->
            <FilterDashboard
              v-model:show="showFilters"
              @filter-change="handleFilter"
              @filter-reset="handleFilterReset"
              :topics="topics"
              :materials-amount="materialsAmount"
              :active-filters="activeFilters"
              :asset-types="assetTypesWithCounts"
              :image-sources="imageSourcesWithCounts"
              class="filter-dashboard"
            />
          </div>
        </div>

        <!-- Content column - for all screen sizes -->
        <div :class="isSmallScreen ? '' : 'content-col'">
          <div
            :class="contentClasses"
            ref="materialContainer"
          >
            <!-- Loading state -->
            <div
              v-if="isLoading && materials.length === 0"
              class="d-flex justify-center align-center empty-state-container"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              />
            </div>

            <!-- Error state -->
            <v-alert
              v-else-if="queryError"
              type="error"
              class="empty-state-container mx-4"
              :text="
                retryCount >= 3
                  ? $t('ad-materials.messages.service-unavailable')
                  : $t('ad-materials.messages.error-loading')
              "
              variant="tonal"
              border="start"
              density="comfortable"
            >
              <template v-slot:append>
                <v-btn
                  v-if="retryCount < 3"
                  color="error"
                  variant="tonal"
                  class="font-weight-bold"
                  @click="handleRetry"
                >
                  {{ $t('ad-materials.actions.retry') }}
                </v-btn>
              </template>
            </v-alert>

            <!-- Empty state -->
            <v-alert
              v-else-if="!flattenedMaterials.length && !isLoading"
              type="info"
              class="empty-state-container"
              variant="tonal"
              border="start"
              density="comfortable"
            >
              <template v-slot:prepend>
                <div class="d-flex align-center justify-center h-100">
                  <v-icon
                    color="info"
                    size="large"
                    >mdi-information-outline</v-icon
                  >
                </div>
              </template>
              <div class="d-flex align-center">
                {{ $t('ad-materials.messages.no-materials') }}
              </div>
            </v-alert>

            <!-- Materials grid -->
            <div
              v-else
              class="materials-container"
            >
              <v-row>
                <v-col
                  v-for="item in flattenedMaterials"
                  :key="item.id"
                  cols="12"
                  sm="6"
                  md="6"
                  lg="4"
                  xl="3"
                >
                  <MaterialCard
                    :name="item.displayName"
                    :url="item.url"
                    :topicId="item.topicId"
                    :imageId="isMaterialPublishable(item) ? item.id : undefined"
                    :imageSource="item.imageSource"
                    :caption="item.topicName || ''"
                    :thumbnail-url="item.previewUrl"
                    :is-new="isNewMaterial(item.createdAt)"
                    :icon="item.icon"
                    :can-edit="isUserUploadedMaterial(item) && isEditFeatureEnabled"
                    :can-delete="isDeletableMaterial(item) && isDeleteFeatureEnabled"
                    :is-image="isImageFile(item.url)"
                    :is-video="isVideoFile(item.url) || item.assetType === AssetType.Video"
                    :open-preview-fallback="false"
                    class="h-100"
                    @preview="materialPreviewId = item.id"
                    @edit="handleEditFromCard(item.id)"
                    @delete="handleDeleteFromCard(item.id)"
                  />
                </v-col>
              </v-row>

              <!-- Loading indicator for pagination -->
              <div
                v-if="isLoading && materials.length > 0"
                class="d-flex justify-center py-4 w-100"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                />
              </div>

              <!-- Infinite scroll trigger -->
              <div
                ref="scrollTrigger"
                class="scroll-trigger"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <MaterialPreview
      v-model="materialPreviewId"
      :material="materialPreview"
      :is-image="isMaterialPreviewImage"
      :can-edit="canEditMaterial"
      :can-delete="canDeleteMaterial"
    />
    <EditMaterialDialog
      v-model="showEditDialog"
      :material="materialForEdit"
      :topics="translatedTopics"
      @material-updated="handleMaterialUpdated"
    />
    <DeleteMaterialDialog
      v-model="showDeleteDialog"
      :material="materialForDelete"
      @material-deleted="handleMaterialDeleted"
    />
  </v-container>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useApolloClient, useMutation, useQuery } from '@vue/apollo-composable'
import { computed, inject, onActivated, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LocationQuery } from 'vue-router'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

import useFeature from '@/composables/useFeature'
import { useTopicTranslation } from '@/composables/useTopicTranslation'
import { AssetType } from '@/generated/graphql'
import { debounce } from '@/lib/debounce'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { uploadToS3 } from '@/lib/uploadToS3'
import {
  AD_MATERIALS_FILTERS_KEY,
  loadFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
  type StoredFilters,
} from '@/modules/adMaterials/helpers/adMaterialsFilterStorage'
import MaterialCard from '@/modules/landingpages/MaterialCard.vue'

import AD_MATERIALS from './AdMaterials.gql'
import BaseHeader from './BaseHeader.vue'
import CONFIRM_MULTIPLE_MATERIAL_UPLOADS from './ConfirmMultipleMaterialUploads.gql'
import DeleteMaterialDialog from './DeleteMaterialDialog.vue'
import EditMaterialDialog from './EditMaterialDialog.vue'
import { isImageFile, isVideoFile } from './fileTypes'
import FilterDashboard from './FilterDashboard.vue'
import MaterialPreview from './MaterialPreview.vue'
import REQUEST_MULTIPLE_MATERIAL_UPLOAD_URLS from './RequestMultipleMaterialUploadUrls.gql'
import UploadDialog from './UploadDialog.vue'
import UPLOAD_MATERIALS from './UploadMaterials.gql'

// Define proper type for auth service
interface AuthService {
  user?: {
    companyId: string
  }
}

// Inject auth service with proper typing
const auth = inject<AuthService>('auth')

// Get translation function
const { t } = useI18n()

// Get topic translation helpers
const { getTranslatedTopicName, translateTopics } = useTopicTranslation()

// Translate topics once for all child components
const translatedTopics = computed(() => translateTopics(() => topics.value).value as Topic[])

interface Topic {
  id: string
  name: string
  count?: number
}

interface AssetTypeFilter {
  type: string
  count: number
}

interface ImageSourceFilter {
  source: string
  count: number
}

export interface AdMaterial {
  id: string
  url: string
  displayName: string
  createdAt: string
  updatedAt?: string
  // Fields from AdMaterials query
  previewUrl?: string
  topicName?: string
  imageSource?: string
  topicId?: string
  assetType?: string
  // Fields from UploadMaterial mutation
  previewFile?: {
    id: string
    url: string
    name: string
  }
  type?: string
}

interface AdMaterialsQueryResult {
  adMaterials: {
    adMaterials: AdMaterial[]
    filters: {
      topics: Topic[]
      assetTypes: AssetTypeFilter[]
      imageSources: ImageSourceFilter[]
    }
    meta: {
      total: number
      totalPages: number
    }
  }
}

interface Filters {
  target: 'all' | 'buyers' | 'sellers'
  topics: string[]
  origins: string[]
  search: string
  sort: string
  assetTypes: string[]
}

// Add this new interface for flattened materials
export interface FlattenedMaterial {
  id: string
  displayName: string
  url: string
  previewUrl?: string
  topicId?: string
  topicName?: string
  imageSource?: string
  icon?: string
  assetType?: string
  createdAt: string
}

// Define file type enum for better type safety
type FileType = 'STATIC' | 'VIDEO' | 'IMAGE' | 'DOCUMENT'

// State
const materials = ref<AdMaterial[]>([])
const topics = ref<Topic[]>([]) // Current topics from the latest query
const assetTypesWithCounts = ref<AssetTypeFilter[]>([])
const imageSourcesWithCounts = ref<ImageSourceFilter[]>([])
const showFilters = ref(true)
const currentPage = ref(1)
const hasMorePages = ref(true)
const totalPages = ref(1)
const materialsAmount = ref(0)
const isLoading = ref(false)
const retryCount = ref(0)
const queryFailed = ref(false)
const scrollTrigger = ref<HTMLElement | null>(null)
const materialContainer = ref<HTMLElement | null>(null)
const isSmallScreen = ref(window.innerWidth <= 960)
const materialPreviewId = ref<string>('')

// Add state for upload dialog
const showUploadDialog = ref(false)

// Upload progress state
const uploadProgress = ref(0)
const uploadComplete = ref(false)
const uploadError = ref<string | null>(null)

// Add state for edit and delete dialogs
const showEditDialog = ref(false)
const showDeleteDialog = ref(false)
const materialForEdit = ref<FlattenedMaterial | null>(null)
const materialForDelete = ref<FlattenedMaterial | null>(null)

// Get router instance
const router = useRouter()
const route = useRoute()

// Get feature flag utilities
const { isFeatureActive, featureNameList } = useFeature()

// Add a route changed flag
const routeChanged = ref(false)

// Watch for screen size changes using a more precise approach
watch(
  () => window.innerWidth,
  (width) => {
    if (width <= 960) {
      showFilters.value = false
    } else if (width > 960 && !showFilters.value) {
      showFilters.value = true
    }
  },
  { immediate: true }
)

const defaultFilters: Filters = {
  target: 'all',
  topics: [],
  origins: [],
  search: '',
  sort: 'new-to-old',
  assetTypes: [],
}

const parseTopicIdsFromQuery = (query: LocationQuery): string[] | undefined => {
  if (!query.topicIds) return null

  if (Array.isArray(query.topicIds)) {
    return query.topicIds.filter((id): id is string => typeof id === 'string')
  }

  return [String(query.topicIds)]
}

const urlTopicIds = parseTopicIdsFromQuery(route.query)
const storedFilters = loadFromLocalStorage<StoredFilters | null>(AD_MATERIALS_FILTERS_KEY, null)

const activeFilters = ref<Filters>({
  ...defaultFilters,
  ...(storedFilters && {
    target: storedFilters.target,
    topics: storedFilters.topics ?? [],
    origins: storedFilters.origins ?? [],
    assetTypes: storedFilters.assetTypes ?? [],
  }),
  ...(urlTopicIds && { topics: urlTopicIds }),
})

const amountOfActiveFilters = computed(() => {
  const targetCount = activeFilters.value.target === 'all' ? 0 : 1
  const topicsCount = activeFilters.value.topics.length
  const originsCount = activeFilters.value.origins.length
  const assetsCount = activeFilters.value.assetTypes.length

  return targetCount + topicsCount + originsCount + assetsCount
})

const debouncedSaveToLocalStorage = debounce((value) => {
  saveToLocalStorage(AD_MATERIALS_FILTERS_KEY, value)
}, 500)

watch(
  () => ({
    target: activeFilters.value.target,
    topics: [...activeFilters.value.topics],
    origins: [...activeFilters.value.origins],
    assetTypes: [...activeFilters.value.assetTypes],
  }),
  (persistedFilters) => debouncedSaveToLocalStorage(persistedFilters)
)

// Map frontend sort values to backend sortOrder values
const getSortOrder = (sort: string): string => {
  switch (sort) {
    case 'new-to-old':
      return 'new'
    case 'old-to-new':
      return 'old'
    default:
      return 'new'
  }
}

// Query variables - computed only once and cached
const queryVariables = computed(() => {
  return {
    input: {
      page: currentPage.value,
      itemsPerPage: 20,
      buyers: activeFilters.value.target === 'buyers',
      sellers: activeFilters.value.target === 'sellers',
      imageSources: activeFilters.value.origins,
      topicIds: activeFilters.value.topics,
      search: activeFilters.value.search || '',
      sortOrder: getSortOrder(activeFilters.value.sort),
      assetTypes: activeFilters.value.assetTypes,
    },
  }
})

// Apollo query with reactive variables
const {
  result,
  loading,
  error: queryError,
  refetch,
} = useQuery<AdMaterialsQueryResult>(AD_MATERIALS, () => queryVariables.value, {
  fetchPolicy: 'network-only',
  nextFetchPolicy: 'cache-first',
  notifyOnNetworkStatusChange: true,
})

const isMaterialPublishable = (material: FlattenedMaterial): boolean => {
  return isImageFile(material.url) || isVideoFile(material.url)
}

// Track loading state from Apollo query
watch(loading, (newValue) => {
  isLoading.value = newValue
})

// Watch for query errors and update queryFailed state
watch(queryError, (error) => {
  queryFailed.value = !!error
})

// Update pagination info when query completes
watch(
  result,
  (data) => {
    if (data && data.adMaterials) {
      if (currentPage.value === 1) {
        // Reset materials for first page
        materials.value = data.adMaterials.adMaterials || []

        // Get topics from filters now and filter out Google topic
        if (data.adMaterials.filters && data.adMaterials.filters.topics) {
          topics.value = data.adMaterials.filters.topics.filter(
            (topic) => !topic.name.toLowerCase().includes('google')
          )
        } else {
          topics.value = []
        }

        // Get asset types with counts
        if (data.adMaterials.filters && data.adMaterials.filters.assetTypes) {
          assetTypesWithCounts.value = data.adMaterials.filters.assetTypes
        } else {
          assetTypesWithCounts.value = []
        }

        // Get image sources with counts
        if (data.adMaterials.filters && data.adMaterials.filters.imageSources) {
          imageSourcesWithCounts.value = data.adMaterials.filters.imageSources
        } else {
          imageSourcesWithCounts.value = []
        }
      } else {
        // Append materials for subsequent pages
        materials.value = [...materials.value, ...(data.adMaterials.adMaterials || [])]
      }

      // Update pagination info
      if (data.adMaterials.meta) {
        totalPages.value = data.adMaterials.meta.totalPages || 1
        materialsAmount.value = data.adMaterials.meta.total || 0
        hasMorePages.value = currentPage.value < totalPages.value
      } else {
        hasMorePages.value = false
      }

      // Reset error state on successful query
      if (queryFailed.value) {
        queryFailed.value = false
        retryCount.value = 0
      }
    }
  },
  { immediate: true }
)

// Add Vue Router navigation hooks to handle route changes
onBeforeRouteUpdate((to, from) => {
  // Check if we're navigating to a different route and back
  if (to.name !== from.name) {
    routeChanged.value = true
  }

  // Reset state and refetch data when the route is updated
  resetStateAndRefetch()
})

onBeforeRouteLeave(() => {
  // Clear materials when leaving the route to ensure we refetch when returning
  materials.value = []
  routeChanged.value = true
})

// Add a watch for route changes
watch(
  () => router.currentRoute.value.fullPath,
  () => {
    // If returning to this component after route change
    if (routeChanged.value) {
      resetStateAndRefetch()
      routeChanged.value = false
    }
  }
)

// Add onActivated hook to handle component reactivation with KeepAlive
onActivated(() => {
  // Always refetch when component is activated
  resetStateAndRefetch()
  routeChanged.value = false
})

// Helper function to reset state and refetch data
const resetStateAndRefetch = () => {
  // Reset pagination
  currentPage.value = 1
  hasMorePages.value = true

  // Clear materials to show loading state
  materials.value = []

  // Ensure we are re-triggering the query with clean state
  // Add a small timeout to ensure the component DOM has updated
  setTimeout(() => {
    try {
      // Force a refetch with network-only policy to bypass cache
      refetch({
        fetchPolicy: 'network-only',
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'AdMaterials',
          action: 'resetStateAndRefetch',
        },
      })
    }
  }, 10)
}

const isNewMaterial = (createdAt: string): boolean => {
  if (!createdAt) return false
  const createdDate = new Date(createdAt)
  const now = new Date()
  const oneWeekAgo = new Date(now.setDate(now.getDate() - 7))
  return createdDate >= oneWeekAgo
}

// Update filters and force re-query when required
const updateFilters = (newFilters: Partial<Filters>): void => {
  // Reset to page 1 whenever filters change
  currentPage.value = 1

  // Reset error state when filters change
  if (queryFailed.value) {
    queryFailed.value = false
    retryCount.value = 0
  }

  // Update all filters at once to prevent cascading reactivity
  activeFilters.value = { ...activeFilters.value, ...newFilters }
}

// Define icons for different material types - use string paths instead of imports
const ICONS: Record<FileType, string> = {
  STATIC: '/img/material-static-preview.svg',
  VIDEO: '/img/material-video-preview.svg',
  IMAGE: '/img/material-static-preview.svg',
  DOCUMENT: '/img/material-static-preview.svg',
}

// Flatten materials and their files into a single array
const flattenedMaterials = computed(() => {
  if (!materials.value.length) return []

  const flattened: FlattenedMaterial[] = []

  materials.value.forEach((material) => {
    // Handle both data structures (AdMaterials query and UploadMaterial mutation)
    const previewUrl = material.previewUrl || material.previewFile?.url || ''
    const topicId = material.topicId || ''

    // Get topic name and apply translation for special topics
    const topicName = getTranslatedTopicName(material.topicName || '')

    const assetType = material.assetType || material.type || ''
    const imageSource = material.imageSource || ''

    // Determine file type for icon selection
    let fileType: FileType = 'STATIC'

    // Try to determine file type from URL
    if (material.url) {
      const lowerUrl = material.url.toLowerCase()

      if (lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.mov') || lowerUrl.includes('video')) {
        fileType = 'VIDEO'
      } else if (
        lowerUrl.endsWith('.jpg') ||
        lowerUrl.endsWith('.jpeg') ||
        lowerUrl.endsWith('.png') ||
        lowerUrl.endsWith('.gif') ||
        lowerUrl.endsWith('.webp')
      ) {
        fileType = 'IMAGE'
      } else if (
        lowerUrl.endsWith('.pdf') ||
        lowerUrl.endsWith('.doc') ||
        lowerUrl.endsWith('.docx') ||
        lowerUrl.endsWith('.txt')
      ) {
        fileType = 'DOCUMENT'
      }
    }

    flattened.push({
      ...material,
      // Ensure all required fields are present
      previewUrl,
      topicId,
      topicName,
      assetType,
      imageSource,
      // Always provide an icon for fallback, even when thumbnail exists
      icon: ICONS[fileType],
    })
  })

  return flattened
})

const materialPreview = computed<FlattenedMaterial | null>(() => {
  if (!materialPreviewId.value) return null
  const material = flattenedMaterials.value.find((m) => m.id === materialPreviewId.value)
  return material || null
})

const isMaterialPreviewImage = computed<boolean>(() => {
  if (!materialPreview.value) return false
  // Check both url and previewUrl to determine if it's an image
  const urlToCheck = materialPreview.value.url || materialPreview.value.previewUrl || ''
  if (!urlToCheck) return false
  return isImageFile(urlToCheck)
})

// Check if material can be deleted (user uploaded or AI generated)
const isDeletableMaterial = (material: FlattenedMaterial): boolean => {
  return material.imageSource === 'USER' || material.imageSource === 'AI'
}

// Check if material can be edited (user uploaded only)
const isUserUploadedMaterial = (material: FlattenedMaterial): boolean => {
  return material.imageSource === 'USER'
}

// Check if edit feature is enabled
const isEditFeatureEnabled = computed<boolean>(() => {
  return isFeatureActive(featureNameList.value.AD_MATERIALS_EDIT)
})

// Check if delete feature is enabled
const isDeleteFeatureEnabled = computed<boolean>(() => {
  return isFeatureActive(featureNameList.value.AD_MATERIALS_DELETE)
})

const canEditMaterial = computed<boolean>(() => {
  if (!materialPreview.value) return false
  return isUserUploadedMaterial(materialPreview.value) && isEditFeatureEnabled.value
})

const canDeleteMaterial = computed<boolean>(() => {
  if (!materialPreview.value) return false
  return isDeletableMaterial(materialPreview.value) && isDeleteFeatureEnabled.value
})

// Event handlers - all go through the single updateFilters function
const handleFilter = (filterData: Partial<Filters>): void => {
  updateFilters(filterData)
}

const handleFilterReset = (): void => {
  removeFromLocalStorage(AD_MATERIALS_FILTERS_KEY)
  router.replace({ path: route.path, query: {} })
}

const handleFilterButtonClick = () => {
  showFilters.value = !showFilters.value
}

// Setup mutations
const { mutate: uploadMaterial, loading: _uploading } = useMutation(UPLOAD_MATERIALS)
const { client } = useApolloClient()

// Handle upload button click
const handleUploadButtonClick = () => {
  // Reset upload state when opening dialog
  uploadProgress.value = 0
  uploadComplete.value = false
  uploadError.value = null
  showUploadDialog.value = true
}

// Handle upload start
const handleUploadStart = (data: {
  uploads: Array<{ file: File; topic: Topic; assetType: string }>
}) => {
  // Reset upload state
  uploadProgress.value = 0
  uploadComplete.value = false
  uploadError.value = null

  // Start the actual upload process
  handleUploadComplete(data)
}

// Handle upload complete
const handleUploadComplete = async (data: {
  uploads: Array<{ file: File; topic: Topic; assetType: string }>
}) => {
  try {
    if (!data.uploads || data.uploads.length === 0) {
      Sentry.captureMessage('Upload attempted with no files', {
        level: 'warning',
        tags: { component: 'AdMaterials', action: 'handleUploadComplete' },
      })
      showSnackbarMessage('error', t('ad-materials.upload.error-no-files'))
      return
    }

    const successfulUploads: any[] = []
    const failedUploads: { file: string; error: string }[] = []
    const totalUploads = data.uploads.length

    if (import.meta.env.VITE_APP_STAGE !== 'local') {
      // Presigned URL flow: request all upload URLs → PUT to S3 → confirm all

      // 1. Request upload URLs for all files in one call
      let uploadUrls: { uploadUrl: string; s3Key: string; expiresAt: string }[] = []
      try {
        const urlsResult = await client.mutate({
          mutation: REQUEST_MULTIPLE_MATERIAL_UPLOAD_URLS,
          variables: {
            input: {
              files: data.uploads.map((upload) => ({
                topicId: upload.topic.id,
                mimeType: upload.file.type,
                filename: upload.file.name,
                fileSize: upload.file.size,
                assetType: upload.assetType,
              })),
            },
          },
        })
        uploadUrls = urlsResult.data?.requestMultipleMaterialUploadUrls || []
      } catch (err: any) {
        Sentry.captureException(err, {
          tags: { component: 'AdMaterials', action: 'requestUploadUrls' },
        })
        uploadError.value = t('ad-materials.upload.error-generic')
        return
      }

      // 2. PUT each file directly to S3
      const s3KeysByIndex: { index: number; s3Key: string }[] = []
      for (let i = 0; i < data.uploads.length; i++) {
        uploadProgress.value = Math.round((i / totalUploads) * 80)
        try {
          await uploadToS3(uploadUrls[i].uploadUrl, data.uploads[i].file, data.uploads[i].file.type)
          s3KeysByIndex.push({ index: i, s3Key: uploadUrls[i].s3Key })
        } catch (err: any) {
          Sentry.captureException(err, {
            tags: {
              component: 'AdMaterials',
              action: 'uploadToS3',
              fileName: data.uploads[i].file.name,
            },
          })
          failedUploads.push({
            file: data.uploads[i].file.name,
            error: err.message || 'S3 upload failed',
          })
        }
        uploadProgress.value = Math.round(((i + 1) / totalUploads) * 80)
      }

      // 3. Confirm all successful S3 uploads in one call
      if (s3KeysByIndex.length > 0) {
        try {
          const confirmResult = await client.mutate({
            mutation: CONFIRM_MULTIPLE_MATERIAL_UPLOADS,
            variables: {
              input: {
                uploads: s3KeysByIndex.map(({ index, s3Key }) => ({
                  s3Key,
                  topicId: data.uploads[index].topic.id,
                  mimeType: data.uploads[index].file.type,
                  filename: data.uploads[index].file.name,
                  assetType: data.uploads[index].assetType,
                })),
              },
            },
          })
          const response = confirmResult.data?.confirmMultipleMaterialUploads
          const confirmedMaterials: any[] = response?.materials || []
          confirmedMaterials.forEach((material) => {
            successfulUploads.push({
              ...material,
              topicName: material.topicName ? getTranslatedTopicName(material.topicName) : '',
            })
          })
          // Track per-file errors reported by the backend
          const backendErrors: any[] = response?.errors || []
          backendErrors.forEach((e) => {
            failedUploads.push({ file: e.file || 'unknown', error: e.error || 'Upload failed' })
          })
        } catch (err: any) {
          Sentry.captureException(err, {
            tags: { component: 'AdMaterials', action: 'confirmUploads' },
          })
          s3KeysByIndex.forEach(({ index }) => {
            failedUploads.push({
              file: data.uploads[index].file.name,
              error: err.message || 'Confirm failed',
            })
          })
        }
      }
    } else {
      // Legacy multipart upload flow (local only)
      const companyId = auth?.user?.companyId
      if (!companyId) {
        showSnackbarMessage('error', t('ad-materials.upload.error-no-company'))
        return
      }

      for (let i = 0; i < data.uploads.length; i++) {
        const upload = data.uploads[i]
        try {
          uploadProgress.value = Math.round((i / totalUploads) * 100)
          const result = await uploadMaterial({
            input: {
              file: upload.file,
              topicId: upload.topic.id,
              previewFile: upload.file,
              companyId,
              materialId: '',
              assetType: upload.assetType,
            },
          })
          if (result?.data?.uploadUserTopicMaterial) {
            const material = result.data.uploadUserTopicMaterial
            successfulUploads.push({
              ...material,
              previewUrl: material.previewFile?.url || '',
              topicId: upload.topic.id,
              topicName: getTranslatedTopicName(upload.topic.name),
              assetType: material.type || '',
              imageSource: 'USER',
            })
            uploadProgress.value = Math.round(((i + 1) / totalUploads) * 100)
          }
        } catch (err: any) {
          Sentry.captureException(err, {
            tags: {
              component: 'AdMaterials',
              action: 'uploadSingleFile',
              fileName: upload.file.name,
            },
          })
          failedUploads.push({ file: upload.file.name, error: err.message || 'Unknown error' })
          uploadProgress.value = Math.round(((i + 1) / totalUploads) * 100)
        }
      }
    }

    if (failedUploads.length === 0) {
      uploadProgress.value = 100
      uploadComplete.value = true
    } else if (successfulUploads.length === 0) {
      uploadError.value = t('ad-materials.upload.error-all-failed')
    } else {
      uploadProgress.value = 100
      uploadComplete.value = true
    }

    if (successfulUploads.length > 0) {
      currentPage.value = 1
      materials.value = []
      await refetch({ fetchPolicy: 'network-only' })

      const totalFiles = data.uploads.length
      const successMessage =
        successfulUploads.length === totalFiles
          ? t('ad-materials.upload.success-all', { count: successfulUploads.length })
          : t('ad-materials.upload.success-partial', {
              success: successfulUploads.length,
              total: totalFiles,
            })
      showSnackbarMessage('success', successMessage)
    } else if (failedUploads.length > 0) {
      Sentry.captureMessage('All uploads failed', {
        level: 'error',
        tags: { component: 'AdMaterials', action: 'handleUploadComplete' },
        extra: { failedUploads },
      })
      showSnackbarMessage('error', t('ad-materials.upload.error-all-failed'))
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'AdMaterials', action: 'handleUploadComplete' },
    })
    showSnackbarMessage('error', t('ad-materials.upload.error-generic'))
  }
}

// Retry function for query errors
const handleRetry = async () => {
  retryCount.value++
  try {
    await refetch()
    if (!queryError.value) {
      // Reset retry count if successful
      retryCount.value = 0
      queryFailed.value = false
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'AdMaterials',
        action: 'handleRetry',
        retryCount: retryCount.value,
      },
      extra: {
        queryVariables: queryVariables.value,
      },
    })
  }
}

// Handle edit click from card
const handleEditFromCard = (materialId: string) => {
  const material = flattenedMaterials.value.find((m) => m.id === materialId)
  if (material) {
    materialForEdit.value = material
    showEditDialog.value = true
  }
}

// Handle delete click from card
const handleDeleteFromCard = (materialId: string) => {
  const material = flattenedMaterials.value.find((m) => m.id === materialId)
  if (material) {
    materialForDelete.value = material
    showDeleteDialog.value = true
  }
}

// Handle material updated
const handleMaterialUpdated = async () => {
  try {
    // Reset to page 1 to ensure we see the updated material
    currentPage.value = 1

    // Clear existing materials to show fresh data
    materials.value = []

    // Force a network refresh to get the latest data
    await refetch({
      fetchPolicy: 'network-only',
    })

    // Close the preview dialog
    materialPreviewId.value = ''
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'AdMaterials',
        action: 'handleMaterialUpdated',
      },
    })
  }
}

// Handle material deleted
const handleMaterialDeleted = (materialId: string) => {
  try {
    // Remove the deleted material from the local state immediately
    materials.value = materials.value.filter((m) => m.id !== materialId)

    // Close the preview dialog
    materialPreviewId.value = ''

    // Optionally refetch to ensure consistency
    // You can comment this out if immediate removal is enough
    setTimeout(() => {
      refetch({
        fetchPolicy: 'network-only',
      })
    }, 500)
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'AdMaterials',
        action: 'handleMaterialDeleted',
      },
    })
  }
}

// Computed properties
const contentClasses = computed(() => ({
  'ads-materials-content': true,
  'flex-grow-1': true,
  'full-width': isSmallScreen.value,
  'ml-0': !showFilters.value || isSmallScreen.value,
  'ml-4': showFilters.value && !isSmallScreen.value,
  'transition-width': true,
}))

// Set up window scroll event for infinite scrolling and handle responsive behavior
onMounted(() => {
  // Add window resize event listener for more precise breakpoint control
  const handleResize = () => {
    const width = window.innerWidth
    isSmallScreen.value = width <= 960

    if (width <= 960) {
      showFilters.value = false
    } else if (width > 960) {
      showFilters.value = true
    }
  }

  // Initial check
  handleResize()

  // Add resize listener
  window.addEventListener('resize', handleResize)

  const checkForNewPage = () => {
    if (!scrollTrigger.value || !hasMorePages.value || isLoading.value || queryFailed.value) return

    const scrollTriggerRect = scrollTrigger.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight

    // Check if scroll trigger is visible in viewport (or about to be visible)
    if (scrollTriggerRect.top < viewportHeight + 300) {
      currentPage.value += 1
    }
  }

  // Add scroll event listener
  window.addEventListener('scroll', checkForNewPage)

  // Initial check in case content doesn't fill the page
  setTimeout(checkForNewPage, 500)

  // Clean up event listeners
  onUnmounted(() => {
    window.removeEventListener('scroll', checkForNewPage)
    window.removeEventListener('resize', handleResize)

    // Clear state when component is unmounted
    materials.value = []
    topics.value = []
    currentPage.value = 1
    hasMorePages.value = true
    queryFailed.value = false
    retryCount.value = 0
  })
})
</script>

<style scoped>
.g-0 {
  --v-gutter-x: 0 !important;
}

.filter-col {
  width: 300px !important;
  min-width: 300px;
  max-width: 300px;
  box-sizing: border-box;
  position: relative;
  z-index: 5;
}

.content-col {
  flex: 1;
  position: relative;
  z-index: 1;
}

.filter-wrapper {
  position: sticky;
  top: 0;
  height: calc(100vh - 64px);
  width: 100%;
  box-sizing: border-box;
}

.filter-dashboard {
  position: sticky;
  top: 0;
  height: calc(100vh - 64px);
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
  padding-right: 0;
}

.filter-overlay {
  position: fixed;
  top: 64px;
  left: 0;
  height: calc(100vh - 64px);
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  width: 280px;
  z-index: 100;
}

.full-width {
  width: 100%;
}

@media (max-width: 1279px) {
  .filter-dashboard,
  .filter-wrapper {
    top: 55px;
    height: calc(100vh - 120px);
  }

  .filter-overlay {
    top: 55px;
  }
}

@media (max-width: 959px) {
  .filter-col {
    width: 100% !important;
    min-width: 100%;
    max-width: 100%;
  }

  .filter-wrapper,
  .filter-dashboard {
    position: relative;
    height: auto;
    top: 0;
    width: 100%;
  }

  .ads-materials-content {
    margin-left: 0;
    margin-top: 16px;
  }
}

/* Grid column sizing adjustments */
@media (min-width: 960px) and (max-width: 1263px) {
  .filter-col {
    width: 250px !important;
    min-width: 250px;
    max-width: 250px;
  }

  .filter-wrapper,
  .filter-dashboard {
    width: 250px;
  }
}

.ads-materials-content {
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.empty-state-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  margin: 16px 0;
}

:deep(.v-alert__prepend),
:deep(.v-alert__append) {
  align-self: center !important;
}

.transition-width {
  transition: margin-left 0.3s ease;
}

.materials-container {
  position: relative;
}

.scroll-trigger {
  width: 100%;
  height: 10px;
  margin-top: 20px;
}

@media (min-width: 1904px) {
  .layout-container {
    max-width: 1800px;
  }
}
</style>
